import React, { useContext } from 'react'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { REQUEST_USE_CASE } from '@trustero/trustero-api-web/lib/request/request_pb'
import { Range } from 'react-date-range'
import { StandardButtonSize, StandardButtonVariant } from '../../Buttons'
import { ModalFormId } from '../../../ModalForms'
import { AddEvidenceModal } from '../../../ModalForms/Evidence/AddEvidence/AddEvidenceModal'
import { isOnspringUploadTargetKey } from '../../../ModalForms/Evidence/AddEvidence/AddEvidenceModal.hooks'
import { ReceptorScanSpinner } from '../../../../Throbber/ReceptorScanSpinner'
import { LinkEvidenceToControlModal } from '../../../../pages/Evidence/modals/LinkEvidenceToControlModal'
import ReceptorScanContext from '../../../../context/ReceptorScanStatus'
import { DateRangeFilter } from '../../TrusteroDateRange/DateRangeFilter'
import { FlexRow } from '../../Flex'
import {
  TabPanelButtonGroup,
  TabPanelNoteHeader,
  TabPanelNoteItem,
} from './styles'

export const EvidenceTabNote = ({
  control,
  currentAudit,
  setDateRange,
  dateRange,
  onOpenRequest,
}: {
  control: Control.AsObject
  currentAudit?: AuditRecord
  setDateRange: React.Dispatch<React.SetStateAction<Range>>
  dateRange: Range
  onOpenRequest?: (useCaseType: REQUEST_USE_CASE) => void
}): JSX.Element => {
  const { receptorScanRunning } = useContext(ReceptorScanContext)

  const isNotApplicable = control.status === CONTROL_STATUS.NOTAPPLICABLE
  const controlId = control.id
  const controlModelId = control.modelId
  const receptorFileUpload =
    isOnspringUploadTargetKey(controlModelId) &&
    control.receptorIdsList.length === 1
      ? {
          receptorId: control.receptorIdsList[0],
          uploadTargetKey: controlModelId,
        }
      : undefined
  const name = `${controlModelId} ${control.name}`
  const canAddEvidence = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_ADD_EVIDENCE,
    control,
  )
  const canLinkEvidence = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_LINK_EVIDENCE_CONTROLS,
    control,
  )
  const canAddRequests = useCanPerformAction(CAPABILITY.CAN_ADD_REQUESTS)

  const addEvidenceModalId = isNotApplicable
    ? ModalFormId.NA_INFO
    : ModalFormId.ADD_EVIDENCE

  const linkEvidenceModalId = isNotApplicable
    ? ModalFormId.NA_INFO
    : ModalFormId.LINK_EVIDENCE_TO_CONTROL

  return (
    <>
      <TabPanelNoteItem>
        <TabPanelNoteHeader>
          <DateRangeFilter
            currentAudit={currentAudit?.getId()}
            dateRange={dateRange}
            setDateRange={setDateRange}
            additionalToolTip={!!currentAudit}
            additionalToolTipBody="Auditors only consider evidence and tests from within the date range of your current audit."
          />
          <TabPanelButtonGroup>
            <FlexRow gap={12}>
              <StandardOpenModalButton
                modalId={
                  isNotApplicable
                    ? ModalFormId.NA_INFO
                    : ModalFormId.ADD_DOCUMENT_REQUEST
                }
                variant={StandardButtonVariant.SECONDARY}
                text="Request Evidence"
                size={StandardButtonSize.SMALL}
                requiredPermissions={[]}
                isDisabled={!canAddRequests}
                disabledReason="You do not have permission to request evidence."
                onClick={() => {
                  if (!isNotApplicable) {
                    onOpenRequest?.(REQUEST_USE_CASE.REQUEST_FOR_EVIDENCE)
                  }
                }}
              />
              <StandardOpenModalButton
                modalId={linkEvidenceModalId}
                variant={StandardButtonVariant.SECONDARY}
                text="Link Evidence"
                size={StandardButtonSize.SMALL}
                isDisabled={!canLinkEvidence}
                disabledReason="You do not have permission to link evidence."
              />
              <StandardOpenModalButton
                modalId={addEvidenceModalId}
                text="Upload Evidence"
                size={StandardButtonSize.SMALL}
                isDisabled={!canAddEvidence}
                disabledReason="You do not have permission to add evidence."
              />
            </FlexRow>
          </TabPanelButtonGroup>
        </TabPanelNoteHeader>
        <ReceptorScanSpinner show={receptorScanRunning} />
      </TabPanelNoteItem>
      <AddEvidenceModal
        controlId={controlId}
        name={name}
        openOnDrag
        receptorFileUpload={receptorFileUpload}
      />
      <LinkEvidenceToControlModal controlId={controlId} controlName={name} />
    </>
  )
}
